import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Input, message, Tag } from "antd";
import { EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined, CheckOutlined, CloseOutlined, FileTextOutlined, BankOutlined } from "@ant-design/icons";

import {
  getVendorsApi,
  createVendorApi,
  updateVendorApi,
  deleteVendorApi,
  getVendorPlansApi,
  getVendorsByStatusApi,
  approveVendorApi,
  rejectVendorApi,
  approveVendorBankDetailsApi,
  rejectVendorBankDetailsApi,
} from "../../services/api";

import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const STATUS_FILTERS = ["All", "Pending", "Approved", "Rejected"];
const STATUS_COLORS = { PENDING: "gold", APPROVED: "success", REJECTED: "error" };
const BANK_STATUS_COLORS = { NONE: "default", PENDING: "gold", APPROVED: "success", REJECTED: "error" };

export default function Vendor() {
  const [vendors, setVendors] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [bankTarget, setBankTarget] = useState(null);
  const [bankRejectTarget, setBankRejectTarget] = useState(null);
  const [bankRejectReason, setBankRejectReason] = useState("");

  const [form] = Form.useForm();

  // ================= FETCH =================
  const fetchVendors = async (filter = statusFilter) => {
    const res = filter === "All"
      ? await getVendorsApi()
      : await getVendorsByStatusApi(filter.toUpperCase());
    setVendors((res.data.data || []).slice().sort((a, b) => b.id - a.id));
  };

  const fetchPlans = async () => {
    try {
      const res = await getVendorPlansApi();
      setPlans(res.data.data || []);
    } catch {
      // Non-fatal — the plan dropdown just stays empty if this fails.
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchPlans();
  }, []);

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    const values = await form.validateFields();

    const payload = {
      name: values.name,
      gstNumber: values.gstNumber,
      address: values.address,
      loginEmail: values.loginEmail,
      loginPassword: values.loginPassword,
      planId: values.planId,
    };

    try {
      if (editingVendor) {
        await updateVendorApi(editingVendor.id, payload);
        message.success("Vendor updated");
      } else {
        await createVendorApi(payload);
        message.success("Vendor created — panel access is ready");
      }

      setOpen(false);
      setEditingVendor(null);
      form.resetFields();
      fetchVendors();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save vendor");
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    await deleteVendorApi(id);
    fetchVendors();
  };

  // ================= APPROVE / REJECT =================
  const handleApprove = (record) => {
    Modal.confirm({
      title: `Approve ${record.name}?`,
      content: "They'll be emailed a link to set their password and can then log in.",
      okText: "Approve",
      onOk: async () => {
        try {
          await approveVendorApi(record.id);
          message.success("Vendor approved — password-setup email sent");
          fetchVendors();
        } catch (err) {
          message.error(err?.response?.data?.data || "Failed to approve vendor");
        }
      },
    });
  };

  const openReject = (record) => {
    setRejectTarget(record);
    setRejectReason("");
  };

  const handleReject = async () => {
    try {
      await rejectVendorApi(rejectTarget.id, rejectReason || undefined);
      message.success("Vendor rejected");
      setRejectTarget(null);
      fetchVendors();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to reject vendor");
    }
  };

  // ================= BANK DETAILS APPROVE / REJECT =================
  const handleApproveBank = async (record) => {
    try {
      await approveVendorBankDetailsApi(record.id);
      message.success("Bank details approved — this vendor's settlement account is now live");
      setBankTarget(null);
      fetchVendors();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to approve bank details");
    }
  };

  const openRejectBank = (record) => {
    setBankTarget(null);
    setBankRejectTarget(record);
    setBankRejectReason("");
  };

  const handleRejectBank = async () => {
    try {
      await rejectVendorBankDetailsApi(bankRejectTarget.id, bankRejectReason || undefined);
      message.success("Bank detail request rejected");
      setBankRejectTarget(null);
      fetchVendors();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to reject bank details");
    }
  };

  // ================= TOGGLE ACTIVE =================
  const handleToggleActive = async (record) => {
    try {
      await updateVendorApi(record.id, {
        name: record.name,
        gstNumber: record.gstNumber,
        address: record.address,
        active: !record.active,
      });
      message.success(record.active ? "Vendor deactivated" : "Vendor activated");
      fetchVendors();
    } catch {
      message.error("Failed to update status");
    }
  };

  // ================= EDIT =================
  const handleEdit = (record) => {
    setEditingVendor(record);
    setOpen(true);

    form.setFieldsValue({
      name: record.name,
      gstNumber: record.gstNumber,
      address: record.address,
      planId: record.plan?.id,
    });
  };

  // ================= TABLE =================
  const columns = [
    {
      title: "Vendor",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <div style={{ fontSize: 11, color: "#888" }}>ID: {r.id}</div>
        </div>
      ),
    },
    {
      title: "GST",
      dataIndex: "gstNumber",
      render: (v) => v || "-",
    },
    {
      title: "Address",
      dataIndex: "address",
      render: (v) => v || "-",
    },
    {
      title: "Plan",
      dataIndex: "plan",
      render: (v) => v?.name || "-",
    },
    {
      title: "Usage",
      render: (r) => (
        <span style={{ fontSize: 12, color: "#6B7280" }}>
          {r.activeProductCount ?? 0} / {r.plan?.maxActiveProducts ?? "-"}
        </span>
      ),
    },
    {
      title: "Onboarding",
      dataIndex: "onboardingStatus",
      render: (v) => <Tag color={STATUS_COLORS[v] || "default"}>{v || "APPROVED"}</Tag>,
    },
    {
      title: "Bank Details",
      dataIndex: "bankDetailsStatus",
      render: (v, r) => (
        <span style={{ cursor: "pointer" }} onClick={() => setBankTarget(r)}>
          <Tag color={BANK_STATUS_COLORS[v || "NONE"]}>{v || "NONE"}</Tag>
        </span>
      ),
    },
    {
      title: "Active",
      render: (r) => (
        <span style={{ color: r.active ? "green" : "red" }}>
          {r.active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => {
        const isPending = r.onboardingStatus === "PENDING";
        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                ...(isPending ? [
                  {
                    key: "docs",
                    label: "View Documents",
                    icon: <FileTextOutlined />,
                    onClick: () => {
                      if (r.panDocumentUrl) window.open(r.panDocumentUrl, "_blank");
                      if (r.gstDocumentUrl) window.open(r.gstDocumentUrl, "_blank");
                    },
                  },
                  { key: "approve", label: "Approve", icon: <CheckOutlined />, onClick: () => handleApprove(r) },
                  { key: "reject", label: "Reject", icon: <CloseOutlined />, danger: true, onClick: () => openReject(r) },
                  { type: "divider" },
                ] : []),
                { key: "bank", label: "Bank Details", icon: <BankOutlined />, onClick: () => setBankTarget(r) },
                { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => handleEdit(r) },
                {
                  key: "toggle",
                  label: r.active ? "Deactivate" : "Activate",
                  icon: <PoweroffOutlined />,
                  onClick: () => handleToggleActive(r),
                },
                { type: "divider" },
                { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
              ],
            }}
          >
            <Button icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div className="page-header">
        <div>
          <div className="page-title">Vendors</div>
          <div className="page-sub">{vendors.length} vendors</div>
        </div>

        <Button
          type="primary"
          style={{
            background: "#534ab7",
            border: "none",
            borderRadius: 8,
          }}
          onClick={() => {
            setEditingVendor(null);
            form.resetFields();
            setOpen(true);
          }}
        >
          + Add Vendor
        </Button>
      </div>

      {/* ONBOARDING STATUS FILTER */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            className={`filter-btn${statusFilter === f ? " active" : ""}`}
            onClick={() => { setStatusFilter(f); fetchVendors(f); }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* TABLE */}
      <AppTable columns={columns} data={vendors} />

      {/* MODAL */}
      <Modal
        title={editingVendor ? "Edit Vendor" : "Add Vendor"}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditingVendor(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
      >
        <Form form={form} layout="vertical">
          <AppInput
            name="name"
            label="Vendor Name"
            rules={[{ required: true }]}
          />

          <AppInput name="gstNumber" label="GST Number" />

          <AppInput name="address" label="Address" />

          <AppSelect
            name="planId"
            label="Vendor Plan"
            placeholder="Select a plan"
            options={plans.map((p) => ({ label: `${p.name} (${p.maxActiveProducts} products, ${p.commissionRatePercent}%)`, value: p.id }))}
          />

          <div style={{ marginTop: 8, marginBottom: 4, fontSize: 12, color: "#888", fontWeight: 600 }}>
            PANEL ACCESS {editingVendor && "(leave blank to keep unchanged)"}
          </div>

          <AppInput
            name="loginEmail"
            label="Login Email"
            rules={editingVendor ? [] : [{ required: true, message: "Login email is required" }]}
            placeholder="vendor@example.com"
          />

          <Form.Item
            name="loginPassword"
            label="Login Password"
            rules={editingVendor ? [] : [{ required: true, message: "Login password is required" }]}
          >
            <Input.Password placeholder="Password for the vendor's admin login" />
          </Form.Item>
        </Form>
      </Modal>

      {/* REJECT REASON MODAL */}
      <Modal
        title={rejectTarget ? `Reject ${rejectTarget.name}?` : "Reject vendor?"}
        open={!!rejectTarget}
        okText="Reject"
        okButtonProps={{ danger: true }}
        onOk={handleReject}
        onCancel={() => setRejectTarget(null)}
      >
        <Input.TextArea
          rows={3}
          placeholder="Reason (optional) — helps the sales person understand what to fix"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      {/* BANK DETAILS MODAL */}
      <Modal
        title={bankTarget ? `Bank Details — ${bankTarget.name}` : "Bank Details"}
        open={!!bankTarget}
        onCancel={() => setBankTarget(null)}
        footer={
          bankTarget?.bankDetailsStatus === "PENDING"
            ? [
                <Button key="reject" danger onClick={() => openRejectBank(bankTarget)}>Reject</Button>,
                <Button
                  key="approve"
                  type="primary"
                  style={{ background: "#534ab7", border: "none" }}
                  onClick={() => handleApproveBank(bankTarget)}
                >
                  Approve
                </Button>,
              ]
            : [<Button key="close" onClick={() => setBankTarget(null)}>Close</Button>]
        }
      >
        {bankTarget && (
          <div>
            <Tag color={BANK_STATUS_COLORS[bankTarget.bankDetailsStatus || "NONE"]} style={{ marginBottom: 12 }}>
              {bankTarget.bankDetailsStatus || "NONE"}
            </Tag>

            {bankTarget.bankAccountNumber && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#888", marginBottom: 6 }}>CURRENT (LIVE)</div>
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.8 }}>
                  <div><strong>{bankTarget.bankAccountHolderName}</strong></div>
                  <div>A/C: {bankTarget.bankAccountNumber}</div>
                  <div>IFSC: {bankTarget.bankIfsc} · {bankTarget.bankName}</div>
                  {bankTarget.upiId && <div>UPI: {bankTarget.upiId}</div>}
                </div>
              </div>
            )}

            {bankTarget.bankDetailsStatus === "PENDING" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", marginBottom: 6 }}>
                  {bankTarget.bankAccountNumber ? "REQUESTED CHANGE (AWAITING APPROVAL)" : "SUBMITTED (AWAITING APPROVAL)"}
                </div>
                <div style={{ background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.8 }}>
                  <div><strong>{bankTarget.pendingBankAccountHolderName}</strong></div>
                  <div>A/C: {bankTarget.pendingBankAccountNumber}</div>
                  <div>IFSC: {bankTarget.pendingBankIfsc} · {bankTarget.pendingBankName}</div>
                  {bankTarget.pendingUpiId && <div>UPI: {bankTarget.pendingUpiId}</div>}
                </div>
              </div>
            )}

            {!bankTarget.bankAccountNumber && bankTarget.bankDetailsStatus !== "PENDING" && (
              <div style={{ color: "#888", fontSize: 13 }}>No bank details on file.</div>
            )}

            {bankTarget.bankDetailsStatus === "REJECTED" && bankTarget.bankDetailsRejectionReason && (
              <div style={{ marginTop: 12, color: "#d32f2f", fontSize: 12.5 }}>
                Rejected: {bankTarget.bankDetailsRejectionReason}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* BANK DETAILS REJECT REASON MODAL */}
      <Modal
        title={bankRejectTarget ? `Reject bank details — ${bankRejectTarget.name}?` : "Reject bank details?"}
        open={!!bankRejectTarget}
        okText="Reject"
        okButtonProps={{ danger: true }}
        onOk={handleRejectBank}
        onCancel={() => setBankRejectTarget(null)}
      >
        <Input.TextArea
          rows={3}
          placeholder="Reason (optional) — shown to the vendor"
          value={bankRejectReason}
          onChange={(e) => setBankRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
